// Tourism Experiences Service - Frontend API client
// Manages tourism experience detail views, social interactions, and bookmarks

import { API_BASE } from '../config/api';
import AuthService from './authService';
import i18n from '../i18n';
import {
  EXPERIENCE_CATEGORIES, ROUTE_TYPES, STOP_TYPES,
  TRANSPORT_MODES, TRANSPORT_PRESENTATION,
} from '../config/experienceCategories';
import { formatCurrencyRange } from '../utils/currencyFormat';
import { formatDistance } from '../utils/distanceFormat';
import { PIN_COLORS } from '../utils/mapPins';

// stop_type → PIN_GLYPHS badge key. Types not listed (visit, waypoint,
// optional_stop) wear no badge.
const STOP_TYPE_BADGES = {
  boarding: 'flag',
  alighting: 'stop',
  viewpoint: 'eye',
  photo_stop: 'camera',
  break: 'cup',
  transfer: 'transfer',
};

class TourismExperiencesService {
  static getAuthHeaders() {
    return {
      ...AuthService.getAuthHeaders(),
      'Content-Type': 'application/json'
    };
  }

  static async getCurrentUserId() {
    const user = await AuthService.getCurrentUser();
    return user?.id || null;
  }

  // ==================== EXPERIENCE RETRIEVAL ====================

  // `extras`: the tour page's reads beyond the tour (travelers, tips, your
  // trip, stop tickets, nearby) — only the page asks; the planner's callers
  // need the tour alone.
  static async getExperience(experienceId, { extras = false } = {}) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/tourism-experiences/${experienceId}?userId=${userId || ''}${extras ? '&extras=1' : ''}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get experience' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting experience:', error);
      return { success: false, error: 'Failed to get experience' };
    }
  }

  static async getExperienceByShareCode(shareCode, { extras = false } = {}) {
    try {
      const userId = await this.getCurrentUserId();
      const response = await fetch(
        `${API_BASE}/api/tourism-experiences/share/${shareCode}?userId=${userId || ''}${extras ? '&extras=1' : ''}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get experience' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting experience by share code:', error);
      return { success: false, error: 'Failed to get experience' };
    }
  }

  // ==================== TRIP-BASED QUERIES ====================

  /**
   * Get all tourism experiences for a trip
   */
  static async getExperiencesByTripId(tripId) {
    try {
      const response = await fetch(
        `${API_BASE}/api/tourism-experiences/trip/${tripId}`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get trip experiences' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting trip tourism experiences:', error);
      return { success: false, error: 'Failed to get trip tourism experiences' };
    }
  }

  /**
   * Get user's saved tourism experiences
   */
  static async getSavedExperiences() {
    try {
      const response = await fetch(
        `${API_BASE}/api/tourism-experiences/saved`,
        { headers: await this.getAuthHeaders() }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to get saved experiences' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting saved experiences:', error);
      return { success: false, error: 'Failed to get saved experiences' };
    }
  }

  /**
   * Put a tourism experience on a trip's board (it can be on any number of trips)
   */
  static async associateWithTrip(experienceId, tripId) {
    try {
      const response = await fetch(`${API_BASE}/api/tourism-experiences/${experienceId}/trip`, {
        method: 'PATCH',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ tripId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update experience trip association' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating experience trip association:', error);
      return { success: false, error: 'Failed to update experience trip association' };
    }
  }

  /**
   * Take a tourism experience off one trip's board (trip editors only)
   */
  static async removeFromTrip(experienceId, tripId) {
    try {
      const response = await fetch(`${API_BASE}/api/tourism-experiences/${experienceId}/trip/${tripId}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to remove experience from trip' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing experience from trip:', error);
      return { success: false, error: 'Failed to remove experience from trip' };
    }
  }

  // ==================== SOCIAL INTERACTIONS ====================

  static async toggleLike(experienceId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/tourism-experiences/${experienceId}/like`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to toggle like' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling like:', error);
      return { success: false, error: 'Failed to toggle like' };
    }
  }

  static async saveExperience(experienceId, notes = null) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${API_BASE}/api/tourism-experiences/${experienceId}/save`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify({ userId, notes })
      });

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to save experience' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving experience:', error);
      return { success: false, error: 'Failed to save experience' };
    }
  }

  static async unsaveExperience(experienceId) {
    try {
      const userId = await this.getCurrentUserId();
      if (!userId) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(
        `${API_BASE}/api/tourism-experiences/${experienceId}/save?userId=${userId}`,
        {
          method: 'DELETE',
          headers: await this.getAuthHeaders()
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to unsave experience' };
      }

      return await response.json();
    } catch (error) {
      console.error('Error unsaving experience:', error);
      return { success: false, error: 'Failed to unsave experience' };
    }
  }

  // ==================== HELPERS ====================

  static formatDistance(meters, unit = 'metric') {
    if (!meters) return null;
    return formatDistance(meters, unit);
  }

  static formatDuration(minutes) {
    if (!minutes) return null;
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  }

  static getDifficultyColor(difficulty) {
    switch (difficulty) {
      case 'easy': return { bg: 'bg-success-light', text: 'text-success-dark' };
      case 'moderate': return { bg: 'bg-warning-light', text: 'text-warning-dark' };
      case 'challenging': return { bg: 'bg-error-light', text: 'text-error-dark' };
      default: return { bg: 'bg-surface-secondary', text: 'text-content-secondary' };
    }
  }

  static getTransportIcon(mode) {
    return TRANSPORT_PRESENTATION[mode]?.emoji || '🗺️';
  }

  static getTransportLabel(mode) {
    return TRANSPORT_PRESENTATION[mode]?.label || TRANSPORT_MODES[mode] || mode;
  }

  static getStopTypeLabel(stopType) {
    return STOP_TYPES[stopType] || stopType;
  }

  // The corner badge a stop type wears on the route map, the legend and the
  // stop list (a PIN_GLYPHS key), or null when the stop is just a stop
  // (visit, waypoint, optional). One colour along the route (Kurtis,
  // 2026-10-02, docs/plans/MAP_PINS.md): the type is a mark, not a colour.
  // Replaced the nine-colour getStopMarkerColor and the emoji getStopTypeIcon.
  static getStopBadgeGlyph(stopType) {
    return STOP_TYPE_BADGES[stopType] || null;
  }

  // Optional stops are hollow pins. Either flag marks one: the stop type, or
  // the per-stop is_optional switch on any other type.
  static isOptionalStop(stop) {
    return stop?.stop_type === 'optional_stop' || !!stop?.is_optional;
  }

  // How one stop is drawn, in PlacesMap's place shape: a harbour disc, hollow
  // when optional, with the stop type's ink badge when it has one.
  static getStopMarker(stop) {
    const glyph = TourismExperiencesService.getStopBadgeGlyph(stop?.stop_type);
    return {
      color: PIN_COLORS.harbour,
      shape: TourismExperiencesService.isOptionalStop(stop) ? 'hollow' : 'disc',
      badge: glyph ? { glyph, fill: PIN_COLORS.ink } : null,
    };
  }

  static getRouteTypeLabel(routeType) {
    return ROUTE_TYPES[routeType] || routeType;
  }

  // Falls back to the raw enum string, which is what a category missing from
  // the shared config would render as a chip — so anything added to the DB
  // must be added there too.
  static getCategoryLabel(category) {
    return EXPERIENCE_CATEGORIES[category] || category;
  }

  // Display-producing service function — resolves i18n inside the body per
  // the I18N.md shared-label-module rules (never at module load).
  static formatPrice(min, max, currency) {
    if (min == null && max == null) return null;

    // Both zero = free
    if ((min === 0 || min == null) && (max === 0 || max == null)) {
      return i18n.t('labels:price.free');
    }

    return formatCurrencyRange(min, max, currency || 'USD');
  }
}

export default TourismExperiencesService;
